import {
  Button,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  Palette,
  Tooltip,
  collapseColumn,
  column,
  emptyCell,
  numericColumn,
  simpleHeader,
  useModal,
  useRightPane,
} from "@homebound/beam";
import { Price, emptyCellDash, priceCell } from "src/components";
import { baseDownloadUrl } from "src/context";
import { DisplayNamedFragment } from "src/fragments/commonFragments.generated";
import { BidContractType, BidPackageStatus } from "src/generated/graphqlTypes";
import {
  BidPackageDetailActiveRequestFragment,
  BidPackageDetailPage_BidContractRevisionFragment,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { INTERNAL_ESTIMATE_ID } from "src/routes/bids/plan-based-bid-package/utils";
import { fail, groupBy, isDefined, isNumber, queryResult } from "src/utils";
import { openInSelf } from "src/utils/window";
import { StringParam, useQueryParams } from "use-query-params";
import { UploadBidV2Modal } from "../plan-based-bid-package/components/UploadBidV2Modal";
import { AddTradePartnerModal } from "./AddTradePartnerModal";
import { TradeCostData, sumTradeCostData } from "./BidsTab.utils";
import { EditBidRightPane } from "./EditBidRightPane";
import {
  UnitBasedBidsTable_BidItemFragment,
  UnitBasedBidsTable_BidPackageVersionFragment,
  useUnitBasedBidsTableQuery,
} from "./UnitBasedBidsTable.generated";
import { getBidPackageTradesWithEstimate } from "./utils";

type UnitBasedBidsTableProps = {
  bidPackage: PageBidPackageDetailFragment;
  searchFilter?: string;
  requestsWithEditableBids: BidPackageDetailActiveRequestFragment[];
};

export function UnitBasedBidsTable({ bidPackage, searchFilter, requestsWithEditableBids }: UnitBasedBidsTableProps) {
  const [{ versionId }] = useQueryParams({ versionId: StringParam });

  const query = useUnitBasedBidsTableQuery({
    variables: {
      bidPackageVersionId: versionId ?? bidPackage.latestVersion.id,
      highlightChanges: bidPackage.status.code === BidPackageStatus.Draft && !isDefined(versionId),
    },
  });

  return queryResult(query, ({ bidPackageVersion }) => (
    <UnitBasedBidsTableView
      bidPackage={bidPackage}
      searchFilter={searchFilter}
      requestsWithEditableBids={requestsWithEditableBids}
      bidPackageVersion={bidPackageVersion}
    />
  ));
}

type UnitBasedBidsTableViewProps = UnitBasedBidsTableProps & {
  bidPackageVersion: UnitBasedBidsTable_BidPackageVersionFragment;
};

function UnitBasedBidsTableView({
  bidPackage,
  searchFilter,
  requestsWithEditableBids,
  bidPackageVersion,
}: UnitBasedBidsTableViewProps) {
  const { openModal } = useModal();
  const { openRightPane } = useRightPane();
  const displayingOldVersion = bidPackageVersion.id !== bidPackage.latestVersion.id;
  const isDraft = bidPackage.status.code === BidPackageStatus.Draft;

  function createBidFromXlsx() {
    openModal({ content: <UploadBidV2Modal bidPackage={bidPackage} /> });
  }

  function onEditCost(bidItem: UnitBasedBidsTable_BidItemFragment, tradePartnerId: string) {
    const isEstimateColumn = tradePartnerId === INTERNAL_ESTIMATE_ID;
    const bidPackageRequest = isEstimateColumn
      ? undefined
      : bidPackage.activeRequests.find((r) => r.tradePartner.id === tradePartnerId);
    const bidContractId = isEstimateColumn
      ? bidPackage.internalEstimateBidContract?.id
      : bidPackageRequest?.bidContract?.id;

    const bidContractRevision = bidPackage.latestVersion.latestBidContractRevisions.find(
      (bcr) => bcr.bidContract.id === bidContractId,
    );
    const bidContractLineItem = bidContractRevision?.lineItems.find((li) => li.bidItem?.id === bidItem.id);
    if (!bidItem || (!bidPackageRequest && !isEstimateColumn)) {
      fail("Unable to find item or bid info");
    }
    openRightPane({
      content: (
        <EditBidRightPane
          bidContractLineItem={bidContractLineItem}
          bidItem={bidItem}
          revisionId={bidContractRevision?.id}
          tradeName={bidPackageRequest?.tradePartner.name ?? "Homebound Estimate"}
          newBidContractParams={
            bidContractId
              ? undefined
              : {
                  internalEstimateBidPackageId: isEstimateColumn ? bidPackage.id : undefined,
                  isInternalEstimate: isEstimateColumn,
                  tradePartnerId: bidPackageRequest?.tradePartner.id,
                  bidRequestId: bidPackageRequest?.id,
                  type: BidContractType.UnitBased,
                }
          }
          newBidContractRevisionParams={
            bidContractId
              ? { bidContractId: bidContractId, bidPackageVersionId: bidPackage.latestVersion.id }
              : undefined
          }
        />
      ),
    });
  }

  const showSku = bidPackageVersion.lineItems.some(({ bidItem }) => bidItem.parentMaterialVariant?.modelNumber);

  const rows: GridDataRow<Row>[] = createRows(bidPackage, bidPackageVersion, isDraft);
  const columns: GridColumn<Row>[] = createColumns(
    bidPackage,
    onEditCost,
    () => openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> }),
    isDraft,
    showSku,
    displayingOldVersion,
  );

  return (
    <>
      <div css={Css.df.w100.aic.gap2.py3.p2.$}>
        <div css={Css.xl.$}>Bid Total</div>
        {!isDraft && !displayingOldVersion && (
          <div css={Css.mla.$}>
            <Button
              variant="text"
              label="Upload Costs"
              onClick={createBidFromXlsx}
              disabled={requestsWithEditableBids.length === 0 && "Bids cannot be uploaded"}
            />
          </div>
        )}
      </div>
      <GridTable stickyHeader columns={columns} filter={searchFilter} rows={rows} />
    </>
  );
}

function createRows(
  bidPackage: PageBidPackageDetailFragment,
  bidPackageVersion: UnitBasedBidsTable_BidPackageVersionFragment,
  isDraft: boolean,
): GridDataRow<Row>[] {
  const trades = getBidPackageTradesWithEstimate(bidPackage);
  const tradeIds = trades.map((r) => r.tradePartner.id);
  const previousLineItems = bidPackageVersion.previous?.lineItems ?? [];
  const bidItemRows = bidPackageVersion.lineItems.map(({ bidItem }) => ({
    kind: "bidItem" as const,
    id: bidItem.id,
    data: {
      bidItem,
      // For each bid item line, each trade could have bid at most once on it
      costData: trades.keyBy(
        (trade) => trade.tradePartner.id,
        (trade) =>
          findBidContractLineItemCost(
            bidPackage,
            bidPackageVersion,
            isDraft,
            trade.tradePartner.id,
            bidItem.id,
            trade.bidContractRevision,
          ),
      ),
      isNew: !previousLineItems.some((li) => li.bidItem?.id === bidItem.id),
      isRemoved: false,
    },
  }));

  // If the bid package is in draft status, we want to show removed items as well
  if (isDraft) {
    previousLineItems
      .filter((li) => !bidItemRows.some((bir) => bir.id === li.bidItem?.id))
      .forEach(({ bidItem }) => {
        bidItemRows.push({
          kind: "bidItem" as const,
          id: bidItem.id,
          data: {
            bidItem,
            costData: trades.keyBy(
              (trade) => trade.tradePartner.id,
              (trade) =>
                findBidContractLineItemCost(
                  bidPackage,
                  bidPackageVersion,
                  isDraft,
                  trade.tradePartner.id,
                  bidItem.id,
                  trade.bidContractRevision,
                ),
            ),
            isNew: false,
            isRemoved: true,
          },
        });
      });
  }

  const itemGroupBy = groupBy(bidItemRows, (i) => i.data.bidItem.items[0].id);
  const itemRows = Object.keys(itemGroupBy).map((key) => {
    const children = itemGroupBy[key];
    const item = children[0].data.bidItem.items[0];
    return {
      kind: "item" as const,
      id: key,
      data: {
        item: item,
        costData: children
          // If we are showing removed items, don't sum up their costs
          .filter((c) => !c.data.isRemoved)
          .map((c) => c.data.costData)
          .reduce(sumTradeCostData.bind(null, tradeIds), {}),
      },
      children,
    };
  });

  const costCodeGroupBy = groupBy(itemRows, (o) => o.children[0].data.bidItem.items[0].costCode.id);
  const costCodeRows = Object.keys(costCodeGroupBy).map((key) => {
    const children = costCodeGroupBy[key];
    return {
      kind: "costCode" as const,
      id: key,
      data: {
        costCode: children[0].data.item.costCode,
        costData: children.map((c) => c.data.costData).reduce(sumTradeCostData.bind(null, tradeIds), {}),
      },
      children: children,
    };
  });

  return [simpleHeader, ...costCodeRows];
}

type HeaderRow = { kind: "header" };
type CostCodeRow = { kind: "costCode"; data: { costCode: DisplayNamedFragment; costData: TradeCostData } };
type ItemRow = { kind: "item"; data: { item: DisplayNamedFragment; costData: TradeCostData } };
type BidItemRow = {
  kind: "bidItem";
  data: { bidItem: UnitBasedBidsTable_BidItemFragment; costData: TradeCostData; isNew: boolean; isRemoved: boolean };
};

type Row = HeaderRow | CostCodeRow | ItemRow | BidItemRow;

function createColumns(
  bidPackage: PageBidPackageDetailFragment,
  onEditCost: (bidItem: UnitBasedBidsTable_BidItemFragment, tradePartnerId: string) => void,
  onAddTrade: () => void,
  isDraft: boolean,
  showSku: boolean,
  displayingOldVersion: boolean,
): GridColumn<Row>[] {
  const trades = getBidPackageTradesWithEstimate(bidPackage);
  return [
    collapseColumn<Row>({ bidItem: (data) => ({ content: emptyCell.content, css: highlightChanges(isDraft, data) }) }),
    column<Row>({
      header: "Name",
      costCode: ({ costCode }) => ({ content: costCode.displayName, css: Css.smSb.$ }),
      item: ({ item }) => item.displayName,
      bidItem: ({ bidItem, ...data }) => ({
        content: bidItem.parentMaterialVariant?.displayName ?? bidItem.parentTask?.name ?? bidItem.displayName,
        css: highlightChanges(isDraft, data),
      }),
    }),
    ...(showSku // show sku column if any bid items has a non-empty parentMaterialVariant.modelNumber
      ? [
          column<Row>({
            header: "SKU",
            costCode: emptyCell,
            item: emptyCell,
            bidItem: ({ bidItem, ...data }) => ({
              content: bidItem.parentMaterialVariant?.modelNumber,
              css: highlightChanges(isDraft, data),
            }),
            w: "120px",
          }),
        ]
      : []),
    column<Row>({
      header: "Material Code",
      costCode: emptyCell,
      item: emptyCell,
      bidItem: ({ bidItem, ...data }) => ({
        content: bidItem?.parentMaterialVariant?.code,
        css: highlightChanges(isDraft, data),
      }),
      w: "120px",
    }),
    column<Row>({
      header: "UoM",
      costCode: emptyCell,
      item: emptyCell,
      bidItem: ({ bidItem, ...data }) => ({
        content: bidItem.unitOfMeasure.shortName,
        css: highlightChanges(isDraft, data),
      }),
      w: "67px",
    }),
    ...trades.map((trade) => {
      const onDownloadTemplate = () => {
        const params = new URLSearchParams({
          type: "fullUnitBasedBidPackage",
          bidContractRevisionId: trade.bidContractRevision!.id,
        });
        openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
      };

      return numericColumn<Row>({
        border: "left",
        header: () => (
          <div css={Css.w100.df.aic.$}>
            <div css={Css.mra.$}>{trade.tradePartner.name}</div>
            {trade.bidContractRevision && (
              <Tooltip title={`Download ${trade.tradePartner.name} bid`} placement="top">
                <IconButton icon="download" onClick={onDownloadTemplate} color={Palette.Blue600} />
              </Tooltip>
            )}
          </div>
        ),
        costCode: ({ costData }) =>
          isNumber(costData[trade.tradePartner.id]) ? (
            <div css={Css.sm.$}>
              <Price valueInCents={costData[trade.tradePartner.id]} />
            </div>
          ) : (
            emptyCell
          ),
        item: ({ costData }) =>
          isNumber(costData[trade.tradePartner.id])
            ? priceCell({ valueInCents: costData[trade.tradePartner.id] })
            : emptyCell,
        bidItem: ({ costData }, { row }) => ({
          content: (
            <>
              {!isDraft && !displayingOldVersion && (
                <div css={Css.mra.$}>
                  <IconButton
                    inc={2}
                    icon="pencil"
                    onClick={() => onEditCost(row.data.bidItem, trade.tradePartner.id)}
                  />
                </div>
              )}
              {isNumber(costData[trade.tradePartner.id]) ? (
                <Price valueInCents={costData[trade.tradePartner.id]} />
              ) : (
                emptyCellDash
              )}
            </>
          ),
          css: highlightChanges(isDraft, row.data),
        }),
      });
    }),
    ...(trades.isEmpty
      ? [
          column<Row>({
            header: () => <Button label="Add Trade" variant="text" onClick={onAddTrade} />,
            costCode: emptyCell,
            item: emptyCell,
            bidItem: emptyCell,
            w: "300px",
          }),
        ]
      : []),
  ];
}

function highlightChanges(isDraft: boolean, { isNew, isRemoved }: { isNew: boolean; isRemoved: boolean }) {
  if (isDraft) {
    if (isNew) {
      return Css.bgYellow50.$;
    }
    if (isRemoved) {
      return Css.bgGray50.tdlt.gray500.$;
    }
  }
  return undefined;
}

function findBidContractLineItemCost(
  bidPackage: PageBidPackageDetailFragment,
  bidPackageVersion: UnitBasedBidsTable_BidPackageVersionFragment,
  isDraft: boolean,
  tradePartnerId: string,
  bidItemId: string,
  bidContractRevision?: BidPackageDetailPage_BidContractRevisionFragment,
) {
  // If we are in draft mode we want to pull costs from the previous version revisions
  if (isDraft) {
    const previous = bidPackageVersion.previous;
    const revision =
      tradePartnerId === INTERNAL_ESTIMATE_ID
        ? previous?.latestBidContractRevisions.find(
            (bcr) => bcr.bidContract.id === bidPackage.internalEstimateBidContract?.id,
          )
        : previous?.latestBidContractRevisions.find((bcr) => bcr.bidContract.tradePartner?.id === tradePartnerId);
    const bcli = revision?.lineItems.find((li) => li.bidItem?.id === bidItemId);
    return bcli?.totalCostInCents;
  }
  // find the BCLI for this bidItem
  const bcli = bidContractRevision?.lineItems.find((li) => li.bidItem?.id === bidItemId);
  // this is the unit cost on the BCLI
  return bcli?.totalCostInCents;
}
